import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CHAT_CONFIG, STORAGE_KEYS } from '../constants';
import { executeGetCellarInsights } from '../tools/cellarInsights';
import { executeGetPortfolioBalances } from '../tools/portfolioBalances';
import { executeGetRegionalAllocation } from '../tools/regionalAllocation';
import { executeGetCurrentHoldings } from '../tools/currentHoldings';

export interface ProactiveInsight {
  id: string;
  type: 'maturity' | 'market' | 'performance' | 'concentration' | 'portfolio' | 'allocation';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  suggestedAction?: {
    label: string;
    href: string;
  };
}

interface UseProactiveInsightsOptions {
  enabled?: boolean;
  pollInterval?: number;
}

interface UseProactiveInsightsReturn {
  insights: ProactiveInsight[];
  unreadCount: number;
  markAsRead: (insightId: string) => void;
  dismissInsight: (insightId: string) => void;
  refreshInsights: () => Promise<void>;
  isLoading: boolean;
}

const generateInsightId = (type: string, subject: string): string => {
  return `insight_${type}_${subject.replace(/\s+/g, '_').toLowerCase()}`;
};

// Helper to format currency
const formatCurrencyShort = (value: number): string => {
  if (value >= 1000000) {
    return `£${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(1)}K`;
  }
  return `£${value.toFixed(0)}`;
};

export const useProactiveInsights = (options: UseProactiveInsightsOptions = {}): UseProactiveInsightsReturn => {
  const { enabled = true, pollInterval = CHAT_CONFIG.proactiveInsightInterval } = options;

  const [insights, setInsights] = useState<ProactiveInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shownInsights, setShownInsights] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.sessionShownInsights);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [dismissedInsights, setDismissedInsights] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.dismissedInsights);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Persist shown insights to session storage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.sessionShownInsights, JSON.stringify(Array.from(shownInsights)));
    } catch {
      // Ignore storage errors
    }
  }, [shownInsights]);

  // Persist dismissed insights to local storage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.dismissedInsights, JSON.stringify(Array.from(dismissedInsights)));
    } catch {
      // Ignore storage errors
    }
  }, [dismissedInsights]);

  const fetchCellarInsights = useCallback(async (): Promise<ProactiveInsight[]> => {
    try {
      const result = await executeGetCellarInsights();

      if (!result.success || !result.data) {
        return [];
      }

      const data = result.data as {
        summary: {
          winesAtPeak: number;
          winesPastPeak: number;
          winesWithBidsAboveCost: number;
          strongPerformers: number;
          concentrationWarningsCount: number;
        };
        maturityInsights?: {
          atPeak?: { wines: Array<{ name: string }> };
          pastPeak?: { wines: Array<{ name: string }> };
        };
        marketInsights?: {
          bidsAboveCost?: { wines: Array<{ name: string }> };
        };
        concentrationWarnings?: Array<{ subject: string; percentage: string }>;
      };

      const newInsights: ProactiveInsight[] = [];

      // Wines past peak - high priority
      if (data.summary.winesPastPeak > 0) {
        const wines = data.maturityInsights?.pastPeak?.wines || [];
        newInsights.push({
          id: generateInsightId('maturity', 'past_peak'),
          type: 'maturity',
          title: 'Wines Past Peak',
          description: `${data.summary.winesPastPeak} wine(s) are past their optimal drinking window${
            wines.length > 0 ? `: ${wines[0].name}` : ''
          }.`,
          priority: 'high',
          suggestedAction: {
            label: 'Review in Cellar',
            href: '/my-cellar',
          },
        });
      }

      // Bids above cost - high priority
      if (data.summary.winesWithBidsAboveCost > 0) {
        const wines = data.marketInsights?.bidsAboveCost?.wines || [];
        newInsights.push({
          id: generateInsightId('market', 'bids_above_cost'),
          type: 'market',
          title: 'Profit Opportunity',
          description: `${data.summary.winesWithBidsAboveCost} wine(s) have bids above your purchase cost${
            wines.length > 0 ? `, including ${wines[0].name}` : ''
          }.`,
          priority: 'high',
          suggestedAction: {
            label: 'View Bids',
            href: '/trade',
          },
        });
      }

      // Wines at peak - medium priority
      if (data.summary.winesAtPeak > 0) {
        const wines = data.maturityInsights?.atPeak?.wines || [];
        newInsights.push({
          id: generateInsightId('maturity', 'at_peak'),
          type: 'maturity',
          title: 'Wines at Peak',
          description: `${data.summary.winesAtPeak} wine(s) are in their optimal window${
            wines.length > 0 ? `, such as ${wines[0].name}` : ''
          }.`,
          priority: 'medium',
          suggestedAction: {
            label: 'View Details',
            href: '/my-cellar',
          },
        });
      }

      // Concentration warnings - medium priority
      if (data.summary.concentrationWarningsCount > 0 && data.concentrationWarnings) {
        const firstWarning = data.concentrationWarnings[0];
        newInsights.push({
          id: generateInsightId('concentration', firstWarning?.subject || 'general'),
          type: 'concentration',
          title: 'Portfolio Concentration',
          description: firstWarning
            ? `${firstWarning.subject} represents ${firstWarning.percentage} of your portfolio.`
            : 'Your portfolio has concentration risks.',
          priority: 'medium',
          suggestedAction: {
            label: 'View Allocation',
            href: '/portfolio',
          },
        });
      }

      // Strong performers - low priority (informational)
      if (data.summary.strongPerformers > 0) {
        newInsights.push({
          id: generateInsightId('performance', 'strong_performers'),
          type: 'performance',
          title: 'Strong Performers',
          description: `${data.summary.strongPerformers} wine(s) showing exceptional returns.`,
          priority: 'low',
          suggestedAction: {
            label: 'View Holdings',
            href: '/my-cellar',
          },
        });
      }

      return newInsights;
    } catch {
      return [];
    }
  }, []);

  const fetchPortfolioInsights = useCallback(async (): Promise<ProactiveInsight[]> => {
    try {
      const result = await executeGetPortfolioBalances({});

      if (!result.success || !result.data) {
        return [];
      }

      const data = result.data as {
        balances: Array<{
          portfolio: string;
          currentHoldings: string;
          profitAndLoss: string;
        }>;
        totals?: {
          totalCurrentHoldings: string;
          totalProfitAndLoss: string;
        };
        portfolioCount: number;
      };

      const newInsights: ProactiveInsight[] = [];

      // Parse total P&L to check for significant gains/losses
      if (data.balances && data.balances.length > 0) {
        for (const balance of data.balances) {
          // Extract numeric value from formatted string (e.g., "£15,000.00" -> 15000)
          const pnlMatch = balance.profitAndLoss.match(/[+-]?£?([\d,]+\.?\d*)/);
          const holdingsMatch = balance.currentHoldings.match(/£?([\d,]+\.?\d*)/);

          if (pnlMatch && holdingsMatch) {
            const pnlValue = parseFloat(pnlMatch[1].replace(/,/g, ''));
            const holdingsValue = parseFloat(holdingsMatch[1].replace(/,/g, ''));
            const isNegative = balance.profitAndLoss.includes('-');
            const actualPnl = isNegative ? -pnlValue : pnlValue;

            // Significant loss warning (more than 10% loss)
            if (holdingsValue > 0 && actualPnl < 0) {
              const lossPct = Math.abs(actualPnl / holdingsValue) * 100;
              if (lossPct > 10) {
                newInsights.push({
                  id: generateInsightId('portfolio', `loss_${balance.portfolio}`),
                  type: 'portfolio',
                  title: 'Portfolio Down',
                  description: `${balance.portfolio} is down ${formatCurrencyShort(
                    Math.abs(actualPnl),
                  )} (${lossPct.toFixed(1)}%).`,
                  priority: 'high',
                  suggestedAction: {
                    label: 'Review Portfolio',
                    href: '/portfolio',
                  },
                });
              }
            }

            // Significant gain notification (more than 15% gain)
            if (holdingsValue > 0 && actualPnl > 0) {
              const gainPct = (actualPnl / holdingsValue) * 100;
              if (gainPct > 15) {
                newInsights.push({
                  id: generateInsightId('portfolio', `gain_${balance.portfolio}`),
                  type: 'portfolio',
                  title: 'Strong Returns',
                  description: `${balance.portfolio} is up ${formatCurrencyShort(actualPnl)} (${gainPct.toFixed(1)}%).`,
                  priority: 'low',
                  suggestedAction: {
                    label: 'View Performance',
                    href: '/portfolio',
                  },
                });
              }
            }
          }
        }
      }

      return newInsights;
    } catch {
      return [];
    }
  }, []);

  const fetchAllocationInsights = useCallback(async (): Promise<ProactiveInsight[]> => {
    try {
      const result = await executeGetRegionalAllocation();

      if (!result.success || !result.data) {
        return [];
      }

      const data = result.data as {
        allocations: Array<{
          region: string;
          currentAllocationPct: string;
          profitAndLoss: string;
        }>;
      };

      const newInsights: ProactiveInsight[] = [];

      if (data.allocations && data.allocations.length > 0) {
        // Find regions with high concentration (>40%)
        for (const allocation of data.allocations) {
          const pctMatch = allocation.currentAllocationPct.match(/([\d.]+)/);
          if (pctMatch) {
            const pct = parseFloat(pctMatch[1]);
            if (pct > 40) {
              newInsights.push({
                id: generateInsightId('allocation', `high_${allocation.region}`),
                type: 'allocation',
                title: 'High Concentration',
                description: `${allocation.region} represents ${pct.toFixed(
                  0,
                )}% of your portfolio. Consider diversifying.`,
                priority: 'medium',
                suggestedAction: {
                  label: 'View Allocation',
                  href: '/portfolio',
                },
              });
            }
          }
        }

        // Find best performing region
        let bestRegion = { region: '', pnl: -Infinity };
        for (const allocation of data.allocations) {
          const pnlMatch = allocation.profitAndLoss.match(/[+-]?£?([\d,]+\.?\d*)/);
          if (pnlMatch) {
            const pnlValue = parseFloat(pnlMatch[1].replace(/,/g, ''));
            const isNegative = allocation.profitAndLoss.includes('-');
            const actualPnl = isNegative ? -pnlValue : pnlValue;
            if (actualPnl > bestRegion.pnl) {
              bestRegion = { region: allocation.region, pnl: actualPnl };
            }
          }
        }

        if (bestRegion.pnl > 0) {
          newInsights.push({
            id: generateInsightId('allocation', 'top_region'),
            type: 'allocation',
            title: 'Top Region',
            description: `${bestRegion.region} is your best performing region with ${formatCurrencyShort(
              bestRegion.pnl,
            )} profit.`,
            priority: 'low',
            suggestedAction: {
              label: 'View Details',
              href: '/portfolio',
            },
          });
        }
      }

      return newInsights;
    } catch {
      return [];
    }
  }, []);

  const fetchTopHoldingsInsights = useCallback(async (): Promise<ProactiveInsight[]> => {
    try {
      // Get top performers
      const result = await executeGetCurrentHoldings({
        sortBy: 'profitAndLoss',
        sortDirection: 'desc',
        limit: 5,
      });

      if (!result.success || !result.data) {
        return [];
      }

      const data = result.data as {
        holdings: Array<{
          wine: string; // Combined name + vintage
          profitAndLoss: string; // Combined like "£47,442.5 (+12.5%)"
          totalValue: string;
        }>;
        totalHoldings: number;
      };

      const newInsights: ProactiveInsight[] = [];

      if (data.holdings && data.holdings.length > 0) {
        const topWine = data.holdings[0];
        // profitAndLoss is formatted as "£47,442.5 (+12.5%)"
        const pnlMatch = topWine.profitAndLoss.match(/[+-]?£?([\d,]+\.?\d*)/);

        if (pnlMatch && topWine.wine) {
          const pnlValue = parseFloat(pnlMatch[1].replace(/,/g, ''));
          const isNegative = topWine.profitAndLoss.includes('-');

          if (!isNegative && pnlValue > 500) {
            newInsights.push({
              id: generateInsightId('performance', 'top_wine'),
              type: 'performance',
              title: 'Top Performer',
              description: `${topWine.wine} leads with ${topWine.profitAndLoss} profit.`,
              priority: 'low',
              suggestedAction: {
                label: 'View Holdings',
                href: '/my-cellar',
              },
            });
          }
        }
      }

      return newInsights;
    } catch {
      return [];
    }
  }, []);

  const fetchAllInsights = useCallback(async (): Promise<ProactiveInsight[]> => {
    // Fetch all insights in parallel
    const [cellarInsights, portfolioInsights, allocationInsights, holdingsInsights] = await Promise.all([
      fetchCellarInsights(),
      fetchPortfolioInsights(),
      fetchAllocationInsights(),
      fetchTopHoldingsInsights(),
    ]);

    // Combine and sort by priority
    const allInsights = [...cellarInsights, ...portfolioInsights, ...allocationInsights, ...holdingsInsights];

    // Sort by priority: high -> medium -> low
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    allInsights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    // Limit to top 5 insights to avoid overwhelming the user
    return allInsights.slice(0, 5);
  }, [fetchCellarInsights, fetchPortfolioInsights, fetchAllocationInsights, fetchTopHoldingsInsights]);

  const refreshInsights = useCallback(async (): Promise<void> => {
    if (!enabled) return;

    setIsLoading(true);
    try {
      const newInsights = await fetchAllInsights();
      // Filter out dismissed insights
      const filtered = newInsights.filter((insight) => !dismissedInsights.has(insight.id));
      setInsights(filtered);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, fetchAllInsights, dismissedInsights]);

  // Initial fetch and polling
  useEffect(() => {
    if (!enabled) return;

    // Initial fetch
    refreshInsights();

    // Set up polling
    pollTimerRef.current = setInterval(() => {
      refreshInsights();
    }, pollInterval);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [enabled, pollInterval, refreshInsights]);

  const markAsRead = useCallback((insightId: string): void => {
    setShownInsights((prev) => new Set([...prev, insightId]));
  }, []);

  const dismissInsight = useCallback((insightId: string): void => {
    setDismissedInsights((prev) => new Set([...prev, insightId]));
    setInsights((prev) => prev.filter((i) => i.id !== insightId));
  }, []);

  const unreadCount = useMemo(() => {
    return insights.filter((i) => !shownInsights.has(i.id)).length;
  }, [insights, shownInsights]);

  return {
    insights,
    unreadCount,
    markAsRead,
    dismissInsight,
    refreshInsights,
    isLoading,
  };
};
